import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/** useLayoutEffect in the browser, nothing during the server render (it would warn). */
const useBrowserLayoutEffect = typeof window === 'undefined' ? () => {} : useLayoutEffect;

/** The element client-side navigation moves focus to: the page heading, else <main>. */
export function focusTargetForPage(): HTMLElement | null {
  return document.querySelector<HTMLElement>('main h1') ?? document.getElementById('main');
}

/**
 * Scroll and focus after client-side navigation (BrowserRouter has no scroll
 * restoration of its own):
 *
 *   - a new page (link click, PUSH/REPLACE) starts at the top, or at its
 *     #anchor when the URL has one, and focus moves to the page heading (or
 *     the anchor target) so keyboard and screen-reader users start there;
 *   - Back/Forward (POP) returns to where that page was scrolled, remembered
 *     in memory per history entry (no browser storage).
 *
 * Positions are recorded synchronously: on every scroll event, when this
 * component mounts (a direct load of /product#rent is already scrolled to the
 * anchor), and in the capture phase of a click or popstate — before the
 * router renders the next page and the browser clamps the offset.
 *
 * The very first render is left alone: the browser handles a full page load.
 */
export function RouteEffects() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef(new Map<string, number>());
  const currentKey = useRef(location.key);
  const firstRender = useRef(true);

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    const record = () => positions.current.set(currentKey.current, window.scrollY);
    record();
    window.addEventListener('scroll', record, { passive: true });
    document.addEventListener('click', record, { capture: true });
    window.addEventListener('popstate', record, { capture: true });
    return () => {
      window.removeEventListener('scroll', record);
      document.removeEventListener('click', record, { capture: true });
      window.removeEventListener('popstate', record, { capture: true });
    };
  }, []);

  useBrowserLayoutEffect(() => {
    currentKey.current = location.key;
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (navigationType === 'POP') {
      window.scrollTo(0, positions.current.get(location.key) ?? 0);
      return;
    }
    const anchor = location.hash ? document.getElementById(decodeURIComponent(location.hash.slice(1))) : null;
    if (anchor) anchor.scrollIntoView();
    else window.scrollTo(0, 0);
    const target = anchor ?? focusTargetForPage();
    if (target) {
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }
  }, [location.key]);

  return null;
}
